import React, { FormEvent, ReactElement, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Form, Message } from "semantic-ui-react";

import { AppDispatch } from "redux/store";
import { clearLoginError, login } from "redux/auth/authSlice";
import useTypedSelector from "hooks/useTypedSelector";
import {
  selectAuthNotice,
  selectIsLoggingIn,
  selectLoginError,
} from "redux/selectors/authSelectors";

interface Props {
  onSuccess?: () => void;
}

export default function LoginForm({ onSuccess }: Props): ReactElement {
  const dispatch = useDispatch<AppDispatch>();
  const isLoggingIn = useTypedSelector(selectIsLoggingIn);
  const loginError = useTypedSelector(selectLoginError);
  const notice = useTypedSelector(selectAuthNotice);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // don't show a stale error from a previous attempt when the form reopens
  useEffect(() => {
    dispatch(clearLoginError());
  }, [dispatch]);

  const canSubmit = email.trim() !== "" && password !== "" && !isLoggingIn;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    const result = await dispatch(login({ email, password }));
    if (login.fulfilled.match(result)) {
      setPassword("");
      onSuccess?.();
    }
  };

  return (
    <Form
      data-testid="login-form"
      onSubmit={handleSubmit}
      loading={isLoggingIn}
      error={Boolean(loginError)}
    >
      {notice && (
        <Message info icon="clock outline" content={notice} data-testid="login-notice" />
      )}
      <Form.Input
        id="login-email"
        label="Email"
        type="email"
        name="email"
        autoComplete="username"
        placeholder="you@example.com"
        value={email}
        onChange={(_, { value }) => setEmail(value)}
        autoFocus
        required
      />
      <Form.Input
        id="login-password"
        label="Password"
        type="password"
        name="password"
        autoComplete="current-password"
        value={password}
        onChange={(_, { value }) => setPassword(value)}
        required
      />
      <Message error content={loginError} data-testid="login-error" />
      <Button type="submit" primary fluid disabled={!canSubmit}>
        Log in
      </Button>
    </Form>
  );
}
